import './styles.scss'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'
import { Form } from 'antd'
import { clsx } from 'clsx'

import B2becSpinner from '@/components/B2becSpinner'
import { B2becStepItemLabel, B2becSteps } from '@/components/B2becSteps'
import { ProductService } from '@/services'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { getSelectedMachineData } from '@/store/selectors/serviceCaseSelector'
import { selectDeliveryAddresses } from '@/store/slices/customerSlice'
import { clearServiceCaseConfirmData, resetSelectedMachineInfo, setSelectedMachineInfo } from '@/store/slices/serviceCaseSlice'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useAsync from '@/hooks/useAsync'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDeviceDetect from '@/hooks/useDeviceDetect'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import usePermission from '@/hooks/usePermission'
import { ASYNC_STATUS, SERVICE_ENTRY, SERVICE_STEP } from '@/libs/constants'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { MODULE_SERVICE_RIGHTS } from '@/libs/constants/modulerights'
import { isValidArray } from '@/libs/utils/array'

import AddressAndDateSelectStep from './StepComponents/SelectAddressAndDate'
import MachineSelectStep from './StepComponents/SelectMachine'
import ServiceSelectStep from './StepComponents/SelectService'
import ServiceOverviewStep from './StepComponents/ServiceOverview'
import { onValidateFields } from './validators'

const formItemLayout = {
  labelAlign: 'left',
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
}

const getMachineBasicInfoApi = (materialNumber) => {
  return ProductService.getBasicProductInfo(materialNumber)
}

const RequestServicePage = ({ breadcrumbs }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const { isMobile } = useDeviceDetect()
  const { setPageInfoData } = useAdobeAnalysis()

  useDocumentTitle('pageTitle.requestServices')
  useBreadcrumbs(breadcrumbs)

  const [currentTab, setCurrentTab] = useState(1)

  const selectedMachineData = useAppSelector(getSelectedMachineData)
  const deliveryAddresses = useAppSelector(selectDeliveryAddresses)

  const {
    verifiedPermissions: [canCreateRepairService, canCreateMaintenanceService],
  } = usePermission([
    MODULE_SERVICE_RIGHTS.CREATE_REPAIR_SERVICE_CASE,
    MODULE_SERVICE_RIGHTS.CREATE_MAINTENANCE_SERVICE_CASE,
  ])

  const defaultDeliveryAddress = useMemo(() => {
    if (isValidArray(deliveryAddresses)) {
      return {
        ...deliveryAddresses[0],
        isAddNew: false,
      }
    }
    return { isAddNew: false }
  }, [deliveryAddresses])

  // a new wizard session must not inherit the previous submission's result
  useEffect(() => {
    dispatch(clearServiceCaseConfirmData())
  }, [dispatch])

  useEffect(() => {
    if (selectedMachineData) {
      const { serviceEntry, id } = selectedMachineData
      const pageName = `request service:${serviceEntry}${id && `:${id}`}:step_${currentTab}`

      setPageInfoData({
        pageId: '20',
        pageType: PAGE_TYPES.SERVICE_CASES,
        pageName,
      })
    }
  }, [setPageInfoData, selectedMachineData, currentTab])

  const handleChangeTab = useCallback(
    (tabIndex) => {
      onValidateFields(
        form,
        tabIndex,
        selectedMachineData?.serviceEntry,
        setCurrentTab,
      )
    },
    [form, selectedMachineData.serviceEntry],
  )

  const serviceSteps = useMemo(
    () => [
      {
        label: 'service.selectMachine.tabTitle',
        step: SERVICE_STEP.SELECT_MACHINE,
        content: (
          <MachineSelectStep
            currentTab={currentTab}
            handleChangeTab={handleChangeTab}
          />
        ),
      },
      {
        label: 'service.selectService.tabTitle',
        step: SERVICE_STEP.SELECT_SERVICE,
        content: (
          <ServiceSelectStep
            currentTab={currentTab}
            form={form}
            handleChangeTab={handleChangeTab}
          />
        ),
      },
      {
        label: 'service.addressAndDate.tabTitle',
        step: SERVICE_STEP.SELECT_ADDRESS_n_DATE,
        content: (
          <AddressAndDateSelectStep
            currentTab={currentTab}
            form={form}
            handleChangeTab={handleChangeTab}
            serviceEntry={selectedMachineData?.serviceEntry}
          />
        ),
      },
      {
        label: 'service.serviceOverview.tabTitle',
        step: SERVICE_STEP.SERVICE_OVERVIEW,
        content: (
          <ServiceOverviewStep
            currentTab={currentTab}
            form={form}
            handleChangeTab={handleChangeTab}
          />
        ),
      },
    ],
    [currentTab, handleChangeTab, form, selectedMachineData.serviceEntry],
  )

  const renderServiceProcessSteps = useMemo(() => {
    let filteredServiceSteps = serviceSteps

    switch (selectedMachineData?.serviceEntry) {
      case SERVICE_ENTRY.MAINTENANCE: {
        filteredServiceSteps = serviceSteps.filter(
          (serviceStep) => serviceStep?.step !== SERVICE_STEP.SELECT_SERVICE,
        )
        break
      }
      case SERVICE_ENTRY.MACHINE: {
        filteredServiceSteps = serviceSteps.filter(
          (serviceStep) => serviceStep?.step !== SERVICE_STEP.SELECT_MACHINE,
        )
        break
      }
    }

    return filteredServiceSteps.map((serviceStep, index) => ({
      key: index + 1,
      label: <B2becStepItemLabel index={index + 1} label={t(serviceStep?.label)} />,
      children: serviceStep?.content,
    }))
  }, [selectedMachineData.serviceEntry, serviceSteps, t])

  const { execute, status: getBasicProductInfoStatus } = useAsync(
    getMachineBasicInfoApi,
    false,
  )

  useEffect(() => {
    const fetchMachineBasicInfo = async () => {
      if (
        selectedMachineData?.serviceEntry === SERVICE_ENTRY.MACHINE
        && selectedMachineData?.id
      ) {
        try {
          const { response, error } = await execute(selectedMachineData?.id)

          if (response) {
            dispatch(
              setSelectedMachineInfo({
                name: response?.data?.materialName,
                image: response?.data?.pictureUrl,
              }),
            )
          }

          if (error) {
            console.error('Error fetching machine basic info:', error)
          }
        } catch (error) {
          console.error('Unexpected error fetching machine basic info:', error)
        }
      }
    }

    fetchMachineBasicInfo()
  }, [
    dispatch,
    execute,
    selectedMachineData.id,
    selectedMachineData.serviceEntry,
  ])

  // reset the machine information when changing service entry
  useEffect(() => {
    const { isChangedService, serviceEntry } = selectedMachineData

    // for case restart page and service entry is not stored
    if (!isChangedService && serviceEntry === null) {
      // set service type based on user's right, Repair type by default if available
      const defaultServiceEntry = canCreateRepairService
        ? SERVICE_ENTRY.REPAIR
        : SERVICE_ENTRY.MAINTENANCE
      // if both service types are unavailable, then user will be redirected to 401 page
      dispatch(
        setSelectedMachineInfo({
          serviceEntry: defaultServiceEntry,
        }),
      )
    }

    if (
      selectedMachineData?.isChangedService
      && selectedMachineData?.serviceEntry !== null
    ) {
      // clear the form
      form.resetFields()
      // trigger action to reset data
      dispatch(resetSelectedMachineInfo())
      // redirect back to first tab
      handleChangeTab(1)
    }
  }, [
    form,
    dispatch,
    handleChangeTab,
    selectedMachineData,
    canCreateRepairService,
  ])

  const renderServiceTitle = useCallback(() => {
    if (selectedMachineData?.serviceEntry) {
      switch (selectedMachineData?.serviceEntry) {
        case SERVICE_ENTRY.MAINTENANCE: {
          return <>{t('service.headerMenu.requestMaintenance')}</>
        }
        case SERVICE_ENTRY.MACHINE: {
          return <>{t('createServiceRequest')}</>
        }
        default: {
          return <>{t('service.headerMenu.requestRepair')}</>
        }
      }
    }
    return <>{t('service.headerMenu.requestRepair')}</>
  }, [selectedMachineData.serviceEntry, t])

  const validateMessages = {
    required: t('myProfile.form.validateMessages.required'),
    string: {
      max: t('errors.maxInputExceeded'),
    },
  }

  if (!canCreateMaintenanceService && !canCreateRepairService) {
    return <Navigate replace to="/unauthorized" />
  }

  return getBasicProductInfoStatus === ASYNC_STATUS.PENDING ? <B2becSpinner isLoading /> : (
    <Form
      key={defaultDeliveryAddress?.partnerNumber}
      form={form}
      {...formItemLayout}
      layout="vertical"
      validateMessages={validateMessages}
      initialValues={{
        addressType: 1,
        deliveryAddress: defaultDeliveryAddress,
      }}
    >
      <div
        className={clsx('service-request', {
          'full-width-page-component__wrapper': isMobile,
        })}
      >
        <div className="service-request__wrapper">
          <h1 className="service-request__title service-request__layout--shrink">
            {renderServiceTitle()}
          </h1>
          <B2becSteps
            activeKey={currentTab}
            defaultActiveKey="1"
            onChange={(key) => handleChangeTab(key)}
            items={renderServiceProcessSteps}
          />
        </div>
      </div>
    </Form>
  )
}

export default RequestServicePage
